/**
 * Authentication Store (Zustand)
 *
 * Global state management for zero-knowledge anonymous authentication.
 *
 * Features:
 * - Passphrase-based deterministic UUID generation
 * - localStorage-based session management (no server-side auth)
 * - Same passphrase = same UUID across devices
 * - Complete anonymity with no IP logging
 * - Type-safe with TypeScript
 * - Lightweight and performant
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { user, loading, authenticate, signOut } = useAuthStore()
 *
 *   if (loading) return <Loading />
 *   if (!user) return <LoginPrompt onLogin={authenticate} />
 *   return <UserDashboard user={user} onLogout={signOut} />
 * }
 * ```
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  deriveUUIDFromPassphrase,
  validatePassphrase,
  generatePassphraseSuggestion,
  saveUserUUID,
  clearUserSession,
  getSessionMetadata
} from '@/utils/anonymousAuth'

/**
 * Simplified user object for anonymous auth
 */
export interface AnonymousUser {
  id: string // UUID derived from passphrase
  createdAt: Date // Session creation timestamp
}

export interface AuthState {
  // State
  user: AnonymousUser | null
  loading: boolean
  error: string | null
  initialized: boolean

  // Actions
  authenticate: (passphrase: string) => Promise<boolean>
  signOut: () => void
  generateNewPassphrase: () => string
  validatePassphraseStrength: (passphrase: string) => { isValid: boolean; error?: string }
  initialize: () => void
  clearError: () => void
}

/**
 * Authentication Store
 *
 * Global store for zero-knowledge anonymous authentication.
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // Initial state
      user: null,
      loading: false,
      error: null,
      initialized: false,

      // Initialize auth state from localStorage
      initialize: () => {
        if (get().initialized) return

        try {
          // Check if session exists in localStorage
          const sessionMeta = getSessionMetadata()

          if (sessionMeta) {
            set({
              user: {
                id: sessionMeta.uuid,
                createdAt: sessionMeta.createdAt
              },
              loading: false,
              initialized: true,
            })
          } else {
            set({
              user: null,
              loading: false,
              initialized: true,
            })
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error)
          set({
            loading: false,
            initialized: true,
            error: 'Αποτυχία φόρτωσης συνεδρίας',
          })
        }
      },

      // Authenticate with passphrase (works for both new and existing users)
      authenticate: async (passphrase: string): Promise<boolean> => {
        set({ loading: true, error: null })

        try {
          // Validate passphrase
          const validation = validatePassphrase(passphrase)
          if (!validation.isValid) {
            set({
              error: validation.error || 'Μη έγκυρη φράση πρόσβασης',
              loading: false,
            })
            return false
          }

          // Derive UUID from passphrase
          const uuid = await deriveUUIDFromPassphrase(passphrase)

          // Save to localStorage
          saveUserUUID(uuid)

          // Update state
          set({
            user: {
              id: uuid,
              createdAt: new Date()
            },
            loading: false,
            error: null,
          })

          return true
        } catch (error) {
          console.error('Authentication error:', error)
          set({
            error: 'Σφάλμα ταυτοποίησης. Παρακαλώ δοκιμάστε ξανά.',
            loading: false,
          })
          return false
        }
      },

      // Sign out current user
      signOut: () => {
        clearUserSession()
        set({
          user: null,
          loading: false,
          error: null,
        })
      },

      // Generate a new random passphrase suggestion
      generateNewPassphrase: (): string => {
        return generatePassphraseSuggestion()
      },

      // Validate passphrase strength
      validatePassphraseStrength: (passphrase: string) => {
        return validatePassphrase(passphrase)
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'AuthStore', // Name for Redux DevTools
    }
  )
)

/**
 * Initialize auth store on app startup
 *
 * Call this once in your app's entry point (e.g., App.tsx or main.tsx)
 */
export function initializeAuth() {
  return useAuthStore.getState().initialize()
}
